import { useSyncExternalStore } from "react";

// Release 1.5.0 header: which header panel is open (search dialog, Go to, keyboard shortcuts). One small store so the
// keyboard layer, the header buttons and the panels agree without a provider.

export type HeaderPanel = "search" | "goto" | "shortcuts" | null;

/** The event the Go to palette sends to open the pilot feedback form (PilotFeedbackButton listens). */
export const PILOT_FEEDBACK_OPEN_EVENT = "summitops:pilot-feedback-open";

let panel: HeaderPanel = null;
const listeners = new Set<() => void>();
let inlineSearchFocus: (() => boolean) | null = null;

function emit(): void {
  for (const l of listeners) l();
}

export function openHeaderPanel(next: Exclude<HeaderPanel, null>): void {
  if (panel === next) return;
  panel = next;
  emit();
}

export function closeHeaderPanel(): void {
  if (panel === null) return;
  panel = null;
  emit();
}

export function toggleHeaderPanel(next: Exclude<HeaderPanel, null>): void {
  if (panel === next) closeHeaderPanel();
  else openHeaderPanel(next);
}

export function getHeaderPanel(): HeaderPanel {
  return panel;
}

export function useHeaderPanel(): HeaderPanel {
  return useSyncExternalStore(
    (l) => {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    () => panel,
    () => panel
  );
}

/** The inline (widest screens) search box registers how to focus itself; the function returns false when it is not on screen. */
export function registerInlineSearch(focus: (() => boolean) | null): void {
  inlineSearchFocus = focus;
}

/** Ctrl+K: focus the inline box when it is visible, otherwise open the search dialog. */
export function openSearch(): void {
  if (panel === "search") {
    closeHeaderPanel();
    return;
  }
  if (panel !== null) closeHeaderPanel();
  if (inlineSearchFocus?.()) return;
  openHeaderPanel("search");
}

/** Test helper. */
export function resetHeaderStore(): void {
  panel = null;
  inlineSearchFocus = null;
  emit();
}
